import { useState, useRef } from "react";
import LiveCounter from "./LiveCounter";
import { useI18n } from "@/i18n/I18nProvider";
import { useIntervalWhenVisible } from "@/hooks/useIntervalWhenVisible";

/**
 * Hero turisti — allineata al brief ufficiale.
 * - Nessuna statistica inventata
 * - Focus: intera offerta di un territorio, livello affollamento, punti doppi sulle gemme, verificati
 * - Prima regione: Marche
 */

const GEMME_MARCHE = [
    "Sirolo · Conero",
    "Portonovo · Conero",
    "Urbino · Pesaro-Urbino",
    "Ascoli Piceno · Piceno",
    "Grotte di Frasassi · Genga",
    "Loreto · Ancona",
    "Corinaldo · Ancona",
    "Fermo · Fermano",
    "Fabriano · Anconetano",
    "Recanati · Macerata",
    "Grottammare · Piceno",
    "Offida · Piceno",
];

function FeatureStrip({ t }) {
    return (
        <div
            className="itly-anim delay-4 itly-feature-strip"
            data-testid="hero-features"
            style={{
                marginTop: 60,
                display: "grid",
                gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                gap: 12,
                maxWidth: 820,
                marginLeft: "auto",
                marginRight: "auto",
            }}
        >
            {[
                {
                    icon: (
                        <path d="M12 2a10 10 0 1 0 10 10M12 2v10l7 4" />
                    ),
                    titleKey: "hero.feat1.title",
                    bodyKey: "hero.feat1.body",
                },
                {
                    icon: (
                        <>
                            <path d="M12 2 15 8l7 1-5 4.5 1 7-6-3-6 3 1-7L2 9l7-1z" />
                        </>
                    ),
                    titleKey: "hero.feat2.title",
                    bodyKey: "hero.feat2.body",
                },
                {
                    icon: (
                        <>
                            <path d="M20 6 9 17l-5-5" />
                        </>
                    ),
                    titleKey: "hero.feat3.title",
                    bodyKey: "hero.feat3.body",
                },
            ].map((f) => (
                <div
                    key={f.titleKey}
                    className="itly-feature-tile"
                >
                    <span className="itly-feature-icon" aria-hidden="true">
                        <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            {f.icon}
                        </svg>
                    </span>
                    <div>
                        <div className="itly-feature-title">{t(f.titleKey)}</div>
                        <div className="itly-feature-body">{t(f.bodyKey)}</div>
                    </div>
                </div>
            ))}
        </div>
    );
}

function GemTicker({ t }) {
    const [rotIndex, setRotIndex] = useState(0);
    const tickerRef = useRef(null);
    useIntervalWhenVisible(tickerRef, () => setRotIndex((i) => (i + 1) % GEMME_MARCHE.length), 2400);
    return (
        <div
            ref={tickerRef}
            className="itly-anim delay-5 itly-callout itly-ticker-pill"
            data-testid="hero-ticker"
            style={{
                marginTop: 36,
                display: "inline-flex",
                alignItems: "center",
                gap: 14,
                padding: "10px 18px 10px 14px",
                background: "var(--itly-surface)",
                border: "1px solid var(--itly-border)",
                borderRadius: 999,
                fontFamily: "Inter, sans-serif",
                fontSize: 13.5,
                color: "var(--itly-muted)",
                boxShadow: "var(--itly-card-shadow)",
            }}
        >
            <span
                style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    color: "var(--itly-orange-700)",
                    fontWeight: 600,
                }}
            >
                <span className="itly-pulse-dot" aria-hidden="true" />
                {t("hero.ticker.region")}
            </span>
            <span
                key={rotIndex}
                className="itly-ticker-word"
                style={{
                    color: "var(--itly-fg-strong)",
                    fontWeight: 600,
                }}
            >
                {GEMME_MARCHE[rotIndex]}
            </span>
        </div>
    );
}

export default function HeroTurista() {
    const { t } = useI18n();

    return (
        <section
            id="inizio"
            data-testid="hero-turista"
            style={{
                position: "relative",
                overflow: "hidden",
                background: "var(--itly-page)",
            }}
        >

            <div className="itly-spark-orbit" aria-hidden="true">
                <span className="itly-spark">✦</span>
            </div>

            <div
                style={{
                    position: "relative",
                    zIndex: 1,
                    maxWidth: 1180,
                    margin: "0 auto",
                    padding: "88px 32px 64px",
                    textAlign: "center",
                }}
            >
                <div
                    className="itly-badge itly-anim"
                    data-testid="hero-badge"
                    style={{ margin: "0 auto" }}
                >
                    <span className="spark" aria-hidden="true">✦</span>
                    <span>{t("hero.badge")}</span>
                </div>

                <h1
                    className="itly-serif itly-anim delay-1 itly-hero-h1"
                    data-testid="hero-h1"
                    style={{
                        marginTop: 32,
                        marginBottom: 0,
                        fontSize: "clamp(52px, 7.2vw, 108px)",
                        lineHeight: 0.98,
                        color: "var(--itly-fg-strong)",
                        letterSpacing: "-0.028em",
                    }}
                >
                    {t("hero.h1_a")}
                    <br />
                    <em
                        style={{
                            fontStyle: "italic",
                            color: "var(--itly-orange-500)",
                            fontWeight: 500,
                        }}
                    >
                        {t("hero.h1_b")}
                    </em>
                </h1>

                <p
                    className="itly-anim delay-2"
                    data-testid="hero-sub"
                    style={{
                        marginTop: 28,
                        marginBottom: 0,
                        marginLeft: "auto",
                        marginRight: "auto",
                        fontFamily: "Inter, sans-serif",
                        fontSize: 19,
                        lineHeight: 1.55,
                        color: "var(--itly-muted)",
                        maxWidth: 680,
                    }}
                >
                    {t("hero.sub")}
                </p>

                <p
                    className="itly-anim delay-2 itly-callout"
                    data-testid="hero-mvp-note"
                    style={{
                        marginTop: 18,
                        marginLeft: "auto",
                        marginRight: "auto",
                        maxWidth: 620,
                        fontFamily: "Inter, sans-serif",
                        fontSize: 14,
                        lineHeight: 1.55,
                        color: "var(--itly-fg-strong)",
                        letterSpacing: "0.005em",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "10px 16px",
                        borderRadius: 999,
                        background: "var(--itly-peach-050)",
                        border: "1px solid rgba(232, 113, 76, 0.22)",
                    }}
                >
                    <span aria-hidden="true" style={{ color: "var(--itly-orange-500)", fontSize: 15 }}>✦</span>
                    <span>
                        <strong>{t("hero.mvpBadge")}</strong> — {t("hero.mvpNote")}
                    </span>
                </p>

                <div
                    className="itly-anim delay-3"
                    style={{
                        marginTop: 40,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 12,
                        flexWrap: "wrap",
                    }}
                >
                    <a
                        href="#waitlist"
                        data-testid="hero-cta-primary"
                        className="itly-btn-primary"
                    >
                        {t("hero.ctaJoin")}
                        <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M5 12h14" />
                            <path d="m12 5 7 7-7 7" />
                        </svg>
                    </a>
                    <a
                        href="#mappa"
                        data-testid="hero-cta-secondary"
                        className="itly-btn-secondary"
                    >
                        {t("hero.ctaMap")}
                        <span className="arrow" aria-hidden="true">↓</span>
                    </a>
                </div>

                <div
                    className="itly-anim delay-3"
                    style={{
                        marginTop: 18,
                        display: "flex",
                        justifyContent: "center",
                    }}
                >
                    <LiveCounter testid="hero-live-counter" />
                </div>

                {/* Feature strip — 3 unique pillars from brief */}
                <FeatureStrip t={t} />

                {/* Live ticker — rotating real Marche gemme */}
                <GemTicker t={t} />
            </div>
        </section>
    );
}
